import React, { Fragment, useState, useEffect, memo } from "react";
import { withSpan } from "../../../Apm";
import { cameraService, activityService } from "client-app-core";
import { IconButton, Typography } from "@mui/material";
import Expand from "@mui/icons-material/ZoomOutMap";
import CameraCard from "./components/CameraCard";
import { LinkDialog } from "orion-components/SharedComponents";
import { Translate, getTranslation } from "orion-components/i18n";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { getWidgetState } from "orion-components/Profiles/Selectors";
import { openDialog, closeDialog } from "orion-components/AppState/Actions";
import { getDir } from "orion-components/i18n/Config/selector";
import { removeDockedCameraAndState as removeDockedCamera } from "orion-components/Dock/Actions/index.js";
import { addCameraToDockMode } from "orion-components/Dock/Actions/index.js";
import { setCameraPriority } from "orion-components/Dock/Actions/index.js";
import { fullscreenCameraOpen } from "orion-components/AppState/Selectors";
import { getSelectedContextData } from "orion-components/Profiles/Selectors";
import { loadProfile } from "orion-components/ContextPanel/Actions";
import WidgetMenu from "./components/WidgetMenu";
import { BorderlessCameraCard } from "orion-components/CBComponents";
import { getEcoSettingByKey, getEcosettingsValueByKey } from "orion-components/Services/EcoSettingService/selectors";
import { CollectionItem } from "../../../CBComponents";
import AlertCameraRows from "./components/AlertCameraRows";

const propTypes = {
	id: PropTypes.string,
	unsubscribeFromFeed: PropTypes.func,
	selectFloorPlanOn: PropTypes.func,
	floorPlansWithFacilityFeed: PropTypes.object,
	readOnly: PropTypes.bool,
	entity: PropTypes.object,
	entityType: PropTypes.string,
	selectWidget: PropTypes.func,
	selected: PropTypes.bool,
	canLink: PropTypes.bool,
	useCameraGeometry: PropTypes.bool,
	contextId: PropTypes.string,
	subscriberRef: PropTypes.any,
	disableSlew: PropTypes.bool,
	disableLock: PropTypes.bool,
	unlinkCameras: PropTypes.func,
	eventEnded: PropTypes.bool,
	isAlertProfile: PropTypes.bool,
	otherCameras: PropTypes.array,
	isFloorPlan: PropTypes.bool,
	isEventProfile: PropTypes.bool,
	isRobotProfile: PropTypes.bool,
	secondaryLoadProfile: PropTypes.bool,
	widgetsExpandable: PropTypes.bool,
	alertDateTime: PropTypes.any
};

const defaultProps = {
	selectFloorPlanOn: () => {},
	floorPlansWithFacilityFeed: null,
	otherCameras: [],
	isFloorPlan: false,
	isEventProfile: false,
	isRobotProfile: false,
	alertDateTime: ""
};

const ALERT_CAMERAS_RECORDED_DURATION_SECONDS = "alertCamerasRecordedDurationSeconds";
const ALERT_CAMERAS_LIVE_AND_RECORDED = "alertCamerasLiveAndRecorded";
const MIN_ALERT_CAMERAS_RECORDED_DURATION_SECONDS = 10;
const MAX_ALERT_CAMERAS_RECORDED_DURATION_SECONDS = 600;

const getAlertCamerasRecordedDurationSeconds = (configuredDuration, fallbackDuration) => {
	if (configuredDuration === null || configuredDuration === undefined || configuredDuration === "") {
		return fallbackDuration;
	}

	const parsedDuration = parseInt(configuredDuration, 10);
	if (!Number.isFinite(parsedDuration)) {
		return fallbackDuration;
	}

	return Math.min(
		Math.max(parsedDuration, MIN_ALERT_CAMERAS_RECORDED_DURATION_SECONDS),
		MAX_ALERT_CAMERAS_RECORDED_DURATION_SECONDS
	);
};

const CamerasWidget = ({
	id,
	contextId,
	unsubscribeFromFeed,
	subscriberRef,
	selectWidget,
	selected,
	canLink,
	useCameraGeometry,
	readOnly,
	disableSlew,
	disableLock,
	unlinkCameras,
	eventEnded,
	isAlertProfile,
	selectFloorPlanOn,
	floorPlansWithFacilityFeed,
	otherCameras, // robot cameras widget,
	isFloorPlan,
	isEventProfile,
	isRobotProfile,
	secondaryLoadProfile,
	widgetsExpandable,
	alertDateTime
}) => {
	const [open, setOpen] = useState(null);
	const [camerasLoaded, setCamerasLoaded] = useState(false);

	const entity = useSelector(state => getSelectedContextData(state)(contextId, "entity")) || {};
	let entityType = "";
	if (isFloorPlan) {
		entityType = "facility";
	} else if (isAlertProfile) {
		entityType = "activity";
	} else if (entity) {
		entityType = entity.entityType;
	}
	const { geometry } = entity?.entityData ?? {};

	const enabled = useSelector(state => isAlertProfile || getWidgetState(state)(id, "enabled"));
	const dir = useSelector(state => getDir(state));
	const user = useSelector(state => state?.session?.user?.profile);
	const sidebarOpen = useSelector(state => state.appState.dock.dockData.isOpen);
	const fullscreenCamera = useSelector(state => entity && fullscreenCameraOpen(state));
	const dockedCameras = useSelector(state => state.appState.dock?.cameraDock?.dockedCameras);
	const dialog = useSelector(state => state.appState?.dialog?.openDialog || "");
	const playbackStartOffset = useSelector(state => getEcoSettingByKey("playbackStartOffset")(state) || 15);
	const playbackDuration = useSelector(state => getEcoSettingByKey("playbackDuration")(state) || 30);
	const alertCamerasRecordedDuration = useSelector(state =>
		isAlertProfile ? getEcosettingsValueByKey(state)(ALERT_CAMERAS_RECORDED_DURATION_SECONDS) : null
	);
	const alertCamerasLiveAndRecorded = useSelector(state =>
		isAlertProfile ? getEcosettingsValueByKey(state)(ALERT_CAMERAS_LIVE_AND_RECORDED) : false
	);
	const alertCamerasLiveAndRecordedEnabled = isAlertProfile && alertCamerasLiveAndRecorded === true;
	const alertRecordedDuration = isAlertProfile
		? getAlertCamerasRecordedDurationSeconds(alertCamerasRecordedDuration, playbackDuration)
		: playbackDuration;
	const camerasWidgetMaxStreams = useSelector(
		state => getEcosettingsValueByKey(state)("camerasWidgetMaxStreams") ?? 5
	);

	const useOtherCameras = otherCameras?.length > 0 || isFloorPlan || isAlertProfile || isRobotProfile;
	const eventCameras = useSelector(state =>
		isEventProfile ? getSelectedContextData(state)(contextId, "eventCameras") || [] : null
	);
	const camerasInRange = useSelector(state =>
		!isEventProfile && !useOtherCameras ? getSelectedContextData(state)(contextId, "camerasInRange") || [] : null
	);

	let cameras;

	if (isEventProfile) {
		cameras = eventCameras;
	} else if (useOtherCameras) {
		cameras = otherCameras;
	} else {
		cameras = camerasInRange;
	}

	const dispatch = useDispatch();

	const [alertContextCameras, setAlertContextCameras] = useState([]);

	const allCameras = React.useMemo(() => {
		const merged = isAlertProfile ? [...(cameras ?? []), ...alertContextCameras] : (cameras ?? []);
		// optional: dedupe by id
		const map = new Map(merged.map(c => [c.id, c]));
		return Array.from(map.values());
	}, [isAlertProfile, cameras, alertContextCameras]);

	const compactMode = allCameras.length > camerasWidgetMaxStreams;

	useEffect(() => {
		if (camerasLoaded) return;
		if (!allCameras || allCameras.length === 0) return;

		if (allCameras.length === 1) {
			handleCardExpand(allCameras[0].id);
		}
		setCamerasLoaded(true);
	}, [allCameras, camerasLoaded]);

	useEffect(() => {
		let cancelled = false;

		if (!isAlertProfile) {
			setAlertContextCameras([]);
			return;
		}

		activityService.getActivityDetails(contextId, (err, response) => {
			if (cancelled) return;

			if (err) {
				console.log("ERR", err);
				setAlertContextCameras([]);
				return;
			}

			const cams = (response?.contextEntities ?? []).filter(e => e.entityType === "camera");
			setAlertContextCameras(cams);
		});

		return () => {
			cancelled = true;
		};
	}, [isAlertProfile, contextId]);

	useEffect(() => {
		return () => {
			if (unsubscribeFromFeed) {
				// Event profile uses a different stream that includes both cameras in range and pinned cameras
				dispatch(
					unsubscribeFromFeed(
						contextId,
						entityType === "event" ? "eventCameras" : "camerasInRange",
						subscriberRef
					)
				);
			}
		};
	}, [unsubscribeFromFeed, entityType, contextId, subscriberRef]);

	const handleExpand = () => {
		dispatch(selectWidget(id));
	};

	const handleCardExpand = id => {
		setOpen(open === id ? null : id);
	};

	const handleSlewAll = () => {
		const { coordinates } = geometry;
		allCameras.forEach(camera => {
			// Params: cameraId, latitude, longitude, altitude, speed (0 to 1), callback
			// -- don't slew virtual ptz cams (i.e. wavcam) as they are already locked to target
			if (!(camera.entityData.properties.features && camera.entityData.properties.features.includes("ribbon"))) {
				cameraService.moveGeo(camera.id, coordinates[1], coordinates[0], 0, 1);
			}
		});
	};

	const styles = {
		cameraButton: {
			display: "flex",
			alignItems: "center",
			backgroundColor: "lighten($darkGray, 2 %)"
		},
		widgetExpandButton: {
			width: "auto",
			...(dir === "rtl" && {
				paddingLeft: 0
			}),
			...(dir === "ltr" && {
				paddingRight: 0
			})
		}
	};
	const showFloorPlanOnTargetClick = entity => {
		const { entityData } = entity;
		if (entityData.displayType === "facility" && floorPlansWithFacilityFeed !== null) {
			const floorPlanData = floorPlansWithFacilityFeed[entityData.displayTargetId];
			if (floorPlanData.id === entityData.displayTargetId) {
				dispatch(selectFloorPlanOn(floorPlanData, floorPlanData.facilityFeedId));
			}
		}
	};
	const canSlewAll =
		allCameras &&
		allCameras.findIndex(camera => {
			return camera.entityData.properties.features && camera.entityData.properties.features.includes("auto-slew");
		}) > -1;

	const linkCamera = () => {
		dispatch(openDialog("link-entity-dialog"));
	};

	const handleLaunch = () => {
		// -- camera-wall expects entityType of "shape", but entityType on all shape properties is set to "shapes"
		let et = entityType === "shapes" ? "shape" : entityType;

		// -- different actions based on entity type: ["track", "shapes", "event", "camera", "facility"]
		// -- build out properties based on how the Camera-Wall App does it
		const edProps = entity.entityData.properties;
		const name = entity.name || (edProps ? edProps.name : "Unknown");
		const backupType = et.charAt(0).toUpperCase() + et.slice(1);
		const type = edProps ? edProps.subtype || edProps.type || backupType : backupType;
		const displayName = (name ? name.replace("/", "%2F") : contextId).toUpperCase();

		window.open(`/camera-wall-app/#/entityId/${contextId}/entityName/${displayName}/entityType/${et}/type/${type}`);
	};

	const handleCameraSelect = camera => {
		const displayName = camera.entityData.properties.name
			? camera.entityData.properties.name
			: camera.name || camera.id;
		dispatch(loadProfile(camera.id, displayName, "camera", "profile"));
	};

	return selected || !enabled ? (
		<Fragment />
	) : (
		<div className="widget-wrapper collapsed" data-testid="cameras-widget">
			{!isAlertProfile && (
				<div className="widget-header" data-testid="cameras-widget-header">
					<div className="cb-font-b2">
						<Translate value="global.profiles.widgets.cameras.main.title" />
					</div>
					<div className="widget-header-buttons" data-testid="orion-components-profiles-widgets-cameras-cameras-widget-div">
						{widgetsExpandable && (
							<div className="widget-expand-button">
								<IconButton style={styles.widgetExpandButton} onClick={handleExpand} data-testid="orion-components-profiles-widgets-cameras-cameras-widget-icon-button">
									<Expand />
								</IconButton>
							</div>
						)}

						<WidgetMenu
							dir={dir}
							canSlewAll={canSlewAll}
							readOnly={readOnly}
							disableSlew={disableSlew}
							geometry={geometry}
							handleSlewAll={handleSlewAll}
							linkCamera={linkCamera}
							canLinkCamera={
								["camera", "shapes", "track", "device", "compositeDevice"].includes(entityType) &&
								canLink
							}
							handleLaunch={handleLaunch}
						/>
					</div>
				</div>
			)}
			<div className="widget-content" style={{ display: "flex" }} data-testid="cameras-widget-content">
				{allCameras && allCameras.length > 0 && alertCamerasLiveAndRecordedEnabled ? (
					<AlertCameraRows
						cameras={allCameras}
						contextId={contextId}
						entityType={entityType}
						readOnly={readOnly}
						alertDateTime={alertDateTime}
						playbackStartOffset={playbackStartOffset}
						recordedDuration={alertRecordedDuration}
					/>
				) : allCameras && allCameras.length > 0 && compactMode ? (
					allCameras.map((camera, index) => {
						return (
							<CollectionItem
								key={camera.id}
								primaryText={camera.entityData.properties.name || camera.name || camera.id}
								secondaryText={"Camera"}
								type={"camera"}
								item={camera}
								handleSelect={() => handleCameraSelect(camera)}
								geometry={true}
								dir={dir}
							/>
						);
					})
				) : allCameras && allCameras.length > 0 ? (
					allCameras.map((camera, index) => {
						const cameraDisabled = camera.isDeleted || eventEnded;
						const isNewVideoPlayer = camera.player?.newPlayer ?? false;
						if (isNewVideoPlayer) {
							let alertProps = {};
							if (isAlertProfile) {
								alertProps = {
									alertDateTime: alertDateTime,
									defaultToRecorded: true,
									playbackStartOffset: playbackStartOffset,
									initialRecordedDuration: alertRecordedDuration,
									initialRecordedTimeUnit: "seconds"
								};
							}
							return (
								<BorderlessCameraCard
									contextId={contextId}
									camera={camera}
									entityType={entityType}
									readOnly={readOnly}
									key={camera.id}
									expanded={open === camera.id}
									isOnAlertProfile={isAlertProfile}
									isOnProfile={true}
									{...alertProps}
								/>
							);
						} else {
							return (
								<CameraCard
									contextId={contextId}
									cameraIndex={index}
									canUnlink={canLink}
									canControl={
										!readOnly &&
										user.integrations &&
										user.integrations.find(int => int.intId === camera.feedId) &&
										user.integrations.find(int => int.intId === camera.feedId).permissions &&
										user.integrations
											.find(int => int.intId === camera.feedId)
											.permissions.includes("control")
									}
									entityType={entityType}
									useCameraGeometry={useCameraGeometry}
									geometry={geometry}
									key={camera.id}
									loadProfile={loadProfile}
									secondaryLoadProfile={secondaryLoadProfile}
									unlinkCameras={unlinkCameras}
									camera={camera}
									canExpand={!cameraDisabled}
									handleCardExpand={handleCardExpand}
									canTarget={!cameraDisabled}
									hasMenu={open === camera.id && !cameraDisabled}
									expanded={open === camera.id}
									disableSlew={disableSlew}
									disableLock={disableLock}
									sidebarOpen={sidebarOpen}
									dockedCameras={dockedCameras}
									addCameraToDockMode={addCameraToDockMode}
									removeDockedCamera={removeDockedCamera}
									dialog={dialog}
									readOnly={readOnly}
									subscriberRef={subscriberRef}
									setCameraPriority={setCameraPriority}
									fullscreenCamera={fullscreenCamera}
									dir={dir}
									selectFloorPlanOn={showFloorPlanOnTargetClick}
								/>
							);
						}
					})
				) : (
					<Typography style={{ margin: "12px auto", color: "#fff" }} align="center" variant="caption">
						<Translate value="global.profiles.widgets.cameras.main.noCamsAvailable" />
					</Typography>
				)}
			</div>
			{["camera", "shapes", "track", "device", "compositeDevice"].includes(entityType) && (
				<LinkDialog
					dialog={dialog || ""}
					title={getTranslation("global.profiles.widgets.cameras.main.linkCams")}
					closeDialog={closeDialog}
					entity={entity}
					dir={dir}
				></LinkDialog>
			)}
		</div>
	);
};

CamerasWidget.propTypes = propTypes;
CamerasWidget.defaultProps = defaultProps;

export default memo(withSpan("cameras-widget", "profile-widget")(CamerasWidget));
